import React, { type ReactElement, useEffect, useMemo } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Loader, Notice } from '@deque/cauldron-react';
import styles from './CustomIntegrationHandler.css';
import PageTitle from '../../common/components/PageTitle';
import {
  EXTENSION_LAUNCH_CUSTOM_INTEGRATION,
  PRODUCT_URLS,
  ProductSlugs,
  SupportedIntegrationProductSlugs
} from '../../common/constants';
import { useIntegrations } from '../../app/hooks/useIntegrations';
import Link from '../../common/components/Link';
import { useExtensionInstalled } from '../contexts/ExtensionInstalled';

const CustomIntegrationHandler = (): ReactElement => {
  const { t } = useTranslation();
  const location = useLocation();
  const { testId, url } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      testId: params.get('testId'),
      url: params.get('url')
    };
  }, [location]);

  const { loading, isInstalled } = useExtensionInstalled();

  const {
    loading: integrationsLoading,
    userHasAccessToIntegrationProductSlugs
  } = useIntegrations();
  const userHasAccessToCustomIntegration =
    userHasAccessToIntegrationProductSlugs.includes(
      SupportedIntegrationProductSlugs.customIntegration
    );
  useEffect(() => {
    if (
      integrationsLoading ||
      !userHasAccessToCustomIntegration ||
      loading ||
      !isInstalled
    ) {
      return;
    }

    window.postMessage(
      {
        message: EXTENSION_LAUNCH_CUSTOM_INTEGRATION
      },
      '*'
    );
    if (url) {
      window.location.href = url;
    }
  }, [integrationsLoading, userHasAccessToCustomIntegration, testId, url]);

  if (!loading && !isInstalled) {
    return (
      <Redirect
        to={PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].notInstalled}
      />
    );
  }

  if (!integrationsLoading && !userHasAccessToCustomIntegration) {
    // redirect to home page
    return (
      <Redirect to={PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root} />
    );
  }

  const missingParams = !url;

  return (
    <div className={styles.wrap}>
      <PageTitle title={t('Custom integration')} />
      <div>
        <section>
          {!missingParams ? (
            <Loader />
          ) : (
            <Notice
              type="caution"
              title={t('Missing Parameters')}
              className={styles.notice}
            >
              <p>
                {testId
                  ? t('The required parameter (url) is missing.')
                  : t('The required parameters (url and id) are missing.')}{' '}
                {t(
                  'Either update the URL with the required parameters or contact your administrator.'
                )}
              </p>
              <p>
                {t(
                  "Meanwhile, please enter the website URL directly in your browser's address bar to continue testing."
                )}
              </p>
            </Notice>
          )}
          <h2>
            {missingParams
              ? t('How to Continue Testing')
              : t('Launching {{url}}', { url })}
          </h2>

          <ol>
            {missingParams && (
              <li>
                {t("Enter your desired URL in your browser's address bar")}
              </li>
            )}
            <li>{t('Once the page loads, open axe DevTools Extension')}</li>
            <li>{t('Determine how you want to scan your webpage')}</li>
            <li>
              {t(
                'When finished, click Save to send results to your configured integration'
              )}
            </li>
          </ol>
        </section>
        <div className={styles.separator} />
        <section>
          <h3>{t('Need more help?')}</h3>
          <Trans>
            Learn more about axe DevTool Extension in our{' '}
            <Link url={window.appConfig.SUPPORT_URL || ''} openInNewTab>
              help center
            </Link>
          </Trans>
        </section>
      </div>
    </div>
  );
};

export default CustomIntegrationHandler;
